import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useEffect, type ReactNode } from 'react'
import { Provider } from 'react-redux'
import { ErrorBoundary } from 'react-error-boundary'

import { ErrorFallback } from '@/components/errors/error-fallback'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { TOOLTIP_DELAY_MS } from '@/constants'
import { queryClient } from '@/api'
import { startStatusSync } from '@/modules/projects'
import { startSync } from '@/store'
import { store, syncContext } from '@/store/app-store'

export function AppProviders({ children }: { children: ReactNode }) {

  // Seeds shared storage on first run and keeps this tab in step with the other tab.
  useEffect(() => startSync(store, syncContext), [])
  // Sends a project's new status to the server whenever a step changes it.
  useEffect(() => startStatusSync(store), [])

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <ErrorFallback error={error} onReset={resetErrorBoundary} fullScreen />
      )}
    >
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
            {children}
            <Toaster closeButton position="top-right" />
          </TooltipProvider>
          {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-right" />}
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  )
}